import React, { useMemo, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComment,
  faComments,
  faClipboardList,
  faCheck,
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import useConversations from '../../hooks/useConversations';
import { useMessagesUI } from '../../contexts/MessagesUIContext';
import { fetchOnlineUserIds } from '../Messages/recipientOptions';
import { isTaskConversation, isAgentConversation, isCompletedAssistantThread } from '../Messages/conversationKinds';

const formatPreviewTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const formatTaskStatus = (status) => (status === 'closed' ? 'complete' : status);

const conversationTitle = (conversation) => {
  if (isAgentConversation(conversation) && conversation.object_type === 'general') {
    return conversation.other_participant?.name || conversation.object_label || 'Clinical Assistant';
  }
  if (conversation.object_type === 'general' && conversation.other_participant) {
    const { role, name } = conversation.other_participant;
    return [role, name].filter(Boolean).join(' - ') || conversation.object_label || 'Direct message';
  }
  return conversation.object_label || 'Conversation';
};

const sortByUnreadThenRecent = (items) =>
  [...items].sort((a, b) => {
    if (Boolean(a.is_unread) !== Boolean(b.is_unread)) {
      return a.is_unread ? -1 : 1;
    }
    return new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0);
  });

const ConversationRow = ({ conversation, onOpen, onlineUserIds }) => {
  const unread = Boolean(conversation.is_unread);
  const otherId = conversation.other_participant?.id;
  const otherIsAgent = isAgentConversation(conversation)
    || conversation.other_participant?.is_system;
  const otherOnline = !otherIsAgent && otherId != null && onlineUserIds?.has(Number(otherId));
  const isGeneral = conversation.object_type === 'general';
  const withLabel = conversation.other_participant && !isGeneral
    ? `${conversation.other_participant.role || ''} ${conversation.other_participant.name || ''}`.trim()
    : null;

  return (
    <button
      type="button"
      className={`doctor-conversation-item ${unread ? 'unread' : ''}`}
      onClick={() => onOpen(conversation.id)}
      title={conversationTitle(conversation)}
    >
      <div className="doctor-conversation-item-top">
        {unread ? (
          <span className="doctor-conversation-unread-dot" aria-hidden="true" />
        ) : (
          <span className="doctor-conversation-read-spacer" aria-hidden="true" />
        )}
        <span className="doctor-conversation-label">
          {isGeneral && otherOnline && (
            <span className="messages-online-dot" title="Online (just now)" aria-label="Online" />
          )}
          {conversationTitle(conversation)}
        </span>
        {((isTaskConversation(conversation) && conversation.status)
          || isCompletedAssistantThread(conversation)) && (
          <span className={`doctor-conversation-status status-${conversation.status || 'closed'}`}>
            {formatTaskStatus(conversation.status || 'closed')}
          </span>
        )}
      </div>
      {withLabel && (
        <div className="doctor-conversation-with">
          {otherOnline && (
            <span className="messages-online-dot" title="Online (just now)" aria-label="Online" />
          )}
          with {withLabel}
        </div>
      )}
      <div className="doctor-conversation-preview">
        {conversation.last_message_preview || 'No messages'}
      </div>
      <div className="doctor-conversation-time">
        {formatPreviewTime(conversation.last_message_at)}
      </div>
    </button>
  );
};

const DoctorSection = ({
  title,
  icon,
  items,
  collapsed,
  onToggle,
  emptyText,
  onOpen,
  onlineUserIds,
  limit,
}) => {
  const visible = limit ? items.slice(0, limit) : items;

  return (
    <div className="doctor-conversations-section">
      <button
        type="button"
        className="doctor-conversations-section-header"
        onClick={onToggle}
        aria-expanded={!collapsed}
      >
        <FontAwesomeIcon icon={collapsed ? faChevronRight : faChevronDown} />
        <FontAwesomeIcon icon={icon} />
        <span>{title}</span>
        <span className="doctor-conversations-section-count">{items.length}</span>
      </button>
      {!collapsed && (
        <div className="doctor-conversations-section-body">
          {visible.length === 0 ? (
            <div className="doctor-conversations-empty compact">
              <span>{emptyText}</span>
            </div>
          ) : (
            visible.map((conversation) => (
              <ConversationRow
                key={conversation.id}
                conversation={conversation}
                onOpen={onOpen}
                onlineUserIds={onlineUserIds}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

const DoctorConversationsPanel = () => {
  const { conversations, messageUnreadCount, canViewMessages } = useConversations();
  const { openMessages } = useMessagesUI();
  const [openTasksCollapsed, setOpenTasksCollapsed] = useState(false);
  const [conversationsCollapsed, setConversationsCollapsed] = useState(false);
  const [resolvedCollapsed, setResolvedCollapsed] = useState(true);
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set());

  useEffect(() => {
    if (!canViewMessages) return undefined;

    let cancelled = false;
    const refreshOnline = async () => {
      const ids = await fetchOnlineUserIds();
      if (!cancelled) setOnlineUserIds(ids);
    };

    refreshOnline();
    const interval = setInterval(refreshOnline, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [canViewMessages]);

  const { openTasks, directConversations, resolvedClosed } = useMemo(() => {
    const open = [];
    const direct = [];
    const done = [];

    (conversations || []).forEach((c) => {
      if (isCompletedAssistantThread(c)) {
        done.push(c);
      } else if (isTaskConversation(c)) {
        if (c.status === 'resolved' || c.status === 'closed') done.push(c);
        else open.push(c);
      } else {
        direct.push(c);
      }
    });

    return {
      openTasks: sortByUnreadThenRecent(open),
      directConversations: sortByUnreadThenRecent(direct),
      resolvedClosed: sortByUnreadThenRecent(done),
    };
  }, [conversations]);

  if (!canViewMessages) return null;

  const openConversation = (conversationId) => {
    openMessages({ conversationId });
  };

  return (
    <div className="doctor-conversations-panel">
      <div className="doctor-conversations-header">
        <button
          type="button"
          className="doctor-conversations-title-btn"
          onClick={() => openMessages()}
          title="Open all messages"
        >
          <FontAwesomeIcon icon={faComments} />
          <span>Messages</span>
          {messageUnreadCount > 0 && (
            <span className="doctor-conversations-unread-count" aria-label={`${messageUnreadCount} unread`}>
              {messageUnreadCount}
            </span>
          )}
        </button>
        <button
          type="button"
          className="doctor-conversations-view-all"
          onClick={() => openMessages()}
        >
          View all
        </button>
      </div>

      <div className="doctor-conversations-list">
        <DoctorSection
          title="Conversations"
          icon={faComment}
          items={directConversations}
          collapsed={conversationsCollapsed}
          onToggle={() => setConversationsCollapsed((v) => !v)}
          emptyText="No conversations"
          onOpen={openConversation}
          onlineUserIds={onlineUserIds}
          limit={5}
        />
        <DoctorSection
          title="Open Tasks"
          icon={faClipboardList}
          items={openTasks}
          collapsed={openTasksCollapsed}
          onToggle={() => setOpenTasksCollapsed((v) => !v)}
          emptyText="No open tasks"
          onOpen={openConversation}
          onlineUserIds={onlineUserIds}
          limit={6}
        />
        <DoctorSection
          title="Resolved / Complete"
          icon={faCheck}
          items={resolvedClosed}
          collapsed={resolvedCollapsed}
          onToggle={() => setResolvedCollapsed((v) => !v)}
          emptyText="None yet"
          onOpen={openConversation}
          onlineUserIds={onlineUserIds}
          limit={5}
        />
      </div>
    </div>
  );
};

export default DoctorConversationsPanel;
